import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { normalizeImageUrl } from '../lib/imageUrl';
import { usePage } from '../contexts/CmsContext';
import {
  Award,
  Building2,
  Users,
  GraduationCap,
  Stethoscope,
  Microscope,
  BookOpen,
  Shield,
  ArrowRight,
  Play,
  Quote,
  Download,
  Phone,
  CheckCircle,
  FileText,
  ExternalLink,
  Briefcase } from
'lucide-react';

// Asset URLs from design guidelines
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_medical-campus/artifacts/3qfvzwqh_SSMCH%20Cover%20Photo.webp";
const HOSPITAL_IMAGE = "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=800&q=80";
const LAB_IMAGE = "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=800&q=80";
const LIBRARY_IMAGE = "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80";
const HOSTEL_IMAGE = "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&q=80";
const SPORTS_IMAGE = "https://images.unsplash.com/photo-1461896836934- voices?w=800&q=80";

const trustIndicators = [
{
  icon: Award,
  value: 'Approved',
  label: 'NMC Recognized',
  subtext: 'Recognized by National Medical Commission (NMC)',
  highlight: true
},
{
  icon: Building2,
  value: '930',
  label: 'Beds',
  subtext: 'Teaching Hospital & Multispecialty Services',
  details: '910 Teaching Hospital + 20 Multispecialty'
},
{
  icon: Users,
  value: '200+',
  label: 'Qualified Faculty',
  subtext: 'Experienced Medical Professionals'
},
{
  icon: GraduationCap,
  value: '200',
  label: 'MBBS Seats',
  subtext: '2025–26 Batch',
  extra: '41 PG Seats across 16 Departments'
}];


const whyChooseUs = [
{
  icon: Stethoscope,
  title: 'Early Clinical Exposure',
  description: 'Students gain hands-on clinical experience from the first year, bridging theory and practice effectively.'
},
{
  icon: Microscope,
  title: 'Modern Laboratories',
  description: 'State-of-the-art laboratories equipped with advanced equipment for comprehensive medical training.'
},
{
  icon: BookOpen,
  title: 'Experienced Faculty',
  description: 'Learn from distinguished medical professionals with extensive teaching and clinical experience.'
},
{
  icon: Shield,
  title: 'Safe Residential Campus',
  description: 'Secure, well-maintained campus with separate hostels for boys and girls with 24/7 security.'
}];


const campusLife = [
{ image: HOSTEL_IMAGE, title: 'Hostel Facilities', alt: 'Modern hostel building' },
{ image: LIBRARY_IMAGE, title: 'Central Library', alt: 'Library interior' },
{ image: LAB_IMAGE, title: 'Research Laboratories', alt: 'Students in laboratory' },
{ image: SPORTS_IMAGE, title: 'Sports & Recreation', alt: 'Sports facilities' }];


const testimonials = [
{
  content: 'The clinical exposure we receive here from the very beginning has been invaluable. The teaching hospital provides excellent learning opportunities with diverse cases.',
  author: 'Aanya Sharma',
  batch: 'MBBS · 3rd Year',
  image: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=400&q=80'
},
{
  content: 'The faculty members are highly supportive and accessible. The blend of theoretical knowledge and practical training prepares us well for our medical careers.',
  author: 'Rohan Verma',
  batch: 'MBBS · Final Year',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&q=80'
},
{
  content: 'SSMCH gave me far more than a degree — the hands-on training, mentorship, and community made me a confident clinician ready for real-world practice.',
  author: 'Priya Iyer',
  batch: 'MBBS · Intern',
  image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&q=80'
},
{
  content: 'From dissection halls to bedside teaching, every department here is committed to building strong fundamentals. Truly a transformative journey.',
  author: 'Kabir Patel',
  batch: 'MBBS · 2nd Year',
  image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&q=80'
},
{
  content: 'The campus is welcoming, the hostels are well-managed, and the academic environment fosters genuine curiosity. I am proud to call SSMCH home.',
  author: 'Meera Krishnan',
  batch: 'MBBS · 4th Year',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80'
}];


const Home = () => {
  const { data: homeCms } = usePage('home');
  const { data: facultyCms } = usePage('faculty');
  const { data: academicsCms } = usePage('academics');

  // MBBS Seats tile ← Academics program_highlights item 2 (Annual Intake)
  // Qualified Faculty tile ← Faculty page total_faculty
  // Both stay editable in one place inside the CMS.
  const mbbsSeats = academicsCms?.program_highlights?.[1]?.value;
  const qualifiedFaculty = facultyCms?.total_faculty;

  const trustCards = trustIndicators.map((item, i) => {
    if (i === 2 && qualifiedFaculty) return { ...item, value: qualifiedFaculty };
    if (i === 3 && mbbsSeats) return { ...item, value: mbbsSeats };
    return item;
  });
  const campusLifeItems = (homeCms?.campus_life_images?.length ? homeCms.campus_life_images : campusLife);
  const testimonialItems = (homeCms?.testimonials?.length ? homeCms.testimonials : testimonials);
  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center hero-overlay"
        style={{
          backgroundImage: `url(${normalizeImageUrl(homeCms?.hero?.image) || HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section">

        <div className="container-custom py-20 md:py-32">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
              {homeCms?.hero?.badge || 'NMC Recognized Medical College'}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6 animate-slide-up">
              {homeCms?.hero?.title || 'Building Doctors for the Heart of India'}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed animate-slide-up stagger-1">
              {homeCms?.hero?.subtitle || 'Sukh Sagar Medical College & Hospital is dedicated to nurturing compassionate healthcare professionals through excellence in medical education, research, and patient care.'}
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up stagger-2">
              <Link to="/admissions">
                <Button
                  size="lg"
                  className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-14 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                  data-testid="hero-apply-btn">

                  Apply Now
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#1A202C] rounded-full px-8 h-14 text-lg font-medium transition-all"
                  data-testid="hero-contact-btn">

                  <Phone className="mr-2" size={20} />
                  Contact Admissions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicator Bar */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-white to-[#F8F9FA] border-y border-gray-100" data-testid="trust-bar">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {trustCards.map((item, index) =>
            <div
              key={index}
              className="flex flex-col items-center text-center group"
              data-testid={`trust-item-${index}`}>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 ${
              item.highlight ?
              'bg-gradient-to-br from-[#4ECDC4] to-[#3DBDB4] shadow-lg shadow-[#4ECDC4]/30' :
              'bg-[#4ECDC4]/10'}`
              }>
                  <item.icon className={item.highlight ? 'text-white' : 'text-[#4ECDC4]'} size={28} />
                </div>
                
                {/* Main Value */}
                <div className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] leading-none">
                  {item.value}
                </div>
                
                {/* Label */}
                <p className="text-sm font-semibold text-[#4ECDC4] mt-1 uppercase tracking-wide">
                  {item.label}
                </p>
                
                {/* Subtext */}
                <p className="text-xs text-gray-500 mt-2 max-w-[180px] leading-relaxed">
                  {item.subtext}
                </p>
                
                {/* Details (for Hospital beds) */}
                {item.details &&
              <p className="text-[10px] text-gray-400 mt-1 font-medium">
                    {item.details}
                  </p>
              }
                
                {/* Extra info (for Academic seats) */}
                {item.extra &&
              <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-[10px] text-[#D4A84B] font-semibold">
                      {item.extra}
                    </p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose SSMCH */}
      <section className="section-padding bg-white" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A202C] mb-4">
              Excellence in Medical Education
            </h2>
            <p className="text-gray-600 text-lg">
              Discover what makes SSMCH a premier destination for aspiring medical professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) =>
            <Card
              key={index}
              className="card-hover group border-0 shadow-sm hover:shadow-xl transition-all duration-300"
              data-testid={`feature-card-${index}`}>

                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#4ECDC4]/20 to-[#4ECDC4]/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="text-[#4ECDC4]" size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Hospital Overview */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="hospital-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Teaching Hospital
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                State-of-the-Art Teaching Hospital
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Our multi-specialty teaching hospital serves as the cornerstone of clinical training, providing students with exposure to diverse medical cases and advanced healthcare procedures.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                'Round-the-clock emergency services',
                'Advanced diagnostic and imaging facilities',
                'Specialized departments across all medical disciplines',
                'Patient-centered care with modern amenities'].
                map((item, index) =>
                <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#4ECDC4] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                )}
              </ul>
              <Link to="/hospital">
                <Button
                  className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="hospital-learn-more-btn">

                  Learn More About Our Hospital
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src={normalizeImageUrl(homeCms?.hospital_section?.image) || HOSPITAL_IMAGE}
                  alt="SSMCH Teaching Hospital"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover" />

                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 hidden md:block">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4A84B]/10 rounded-full flex items-center justify-center">
                      <Building2 className="text-[#D4A84B]" size={24} />
                    </div>
                    <div>
                      <p className="font-heading text-2xl font-bold text-[#1A202C]">930 Beds</p>
                      <p className="text-sm text-gray-600">Bed Capacity [Including 20 Multispecialty]</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Life */}
      <section className="section-padding bg-white" data-testid="campus-life-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Campus Life
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A202C] mb-4">
              Life at SSMCH
            </h2>
            <p className="text-gray-600 text-lg">
              Experience a vibrant campus environment that nurtures academic excellence and personal growth.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {campusLifeItems.map((item, index) =>
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer"
              data-testid={`campus-item-${index}`}>

                <img
                src={normalizeImageUrl(item.image)}
                alt={item.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-xl font-semibold text-white">
                    {item.title}
                  </h3>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/campus-life">
              <Button
                variant="outline"
                className="border-2 border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4] hover:text-white rounded-full px-8 h-12 font-medium transition-all"
                data-testid="explore-campus-btn">

                Explore Campus Life
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Dean's Welcome */}
      <section className="section-padding bg-white" data-testid="dean-welcome-section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="flex-shrink-0">
                <div className="w-40 h-52 rounded-xl overflow-hidden shadow-xl border-4 border-[#D4A84B]/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/v0kgpsrn_Dean%20Dr%20Kasar%202.jpg"
                    alt="Dr. Pradeep Kumar Kasar - Dean, SSMCH"
                    className="w-full h-full object-cover object-[25%_top]"
                    data-testid="dean-photo-home"
                  />
                </div>
              </div>
              <div>
                <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  Dean's Message
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A202C] mb-4">
                  Welcome to SSMCH
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Our institution is committed to providing quality medical education that prepares students for the challenges of the medical profession. We nurture not just medical knowledge, but the values of compassion, integrity, and service."
                </p>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-heading font-bold text-[#1A202C]">Dr. Pradeep Kumar Kasar</p>
                    <p className="text-xs text-gray-500">MBBS, MD (Community Medicine)</p>
                    <p className="text-sm text-[#D4A84B] font-medium">Dean, SSMCH</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Student Voices
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A202C] mb-4">
              What Our Students Say
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {testimonialItems.map((item, index) =>
            <div
              key={index}
              className="testimonial-card bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow p-6 pt-12 relative flex flex-col"
              data-testid={`testimonial-${index}`}>

                {/* Circular avatar — half above the card */}
                <div className="absolute -top-10 left-1/2 -translate-x-1/2">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4ECDC4]/20 to-[#D4A84B]/20 p-1 shadow-md">
                    <img
                      src={normalizeImageUrl(item.image) || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&q=80'}
                      alt={item.author}
                      className="w-full h-full rounded-full object-cover ring-2 ring-white"
                    />
                  </div>
                </div>

                <Quote className="text-[#4ECDC4] mb-3 mx-auto" size={22} />
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic flex-1">
                  "{item.content}"
                </p>
                <div className="border-t border-gray-100 pt-3 text-center">
                  <p className="font-semibold text-[#1A202C] text-sm">{item.author}</p>
                  <p className="text-xs text-gray-500">{item.batch}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Latest Newsletter */}
      <section className="py-12 bg-white" data-testid="latest-newsletter">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-lg overflow-hidden rounded-xl">
              <div className="flex flex-col md:flex-row">
                <div className="relative bg-gradient-to-br from-[#D4A84B] via-[#C49A3F] to-[#B08A32] p-10 flex flex-col items-center justify-center md:w-72 shrink-0 overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full translate-y-8 -translate-x-8" />
                  <div className="relative z-10 text-center">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-3">
                      <FileText className="text-white" size={36} />
                    </div>
                    <p className="text-white/80 text-xs font-medium tracking-widest uppercase">Quarterly</p>
                    <p className="text-white font-heading font-bold text-xl mt-1">Issue 8</p>
                  </div>
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-3 py-1 rounded-full text-xs font-medium mb-3 w-fit">
                    Latest Publication
                  </span>
                  <h3 className="font-heading text-xl font-bold text-[#1A202C] mb-2">
                    SSMCH Quarterly Newsletter — Oct to Dec 2024
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Featuring campus highlights, academic achievements, research updates, and institutional milestones from the quarter.
                  </p>
                  <div className="flex gap-3">
                    <a href="https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/le4esjzj_8_SSMCH_Newletter8_Oct24ToDec24.pdf" target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-[#D4A84B] hover:bg-[#C49A3F] text-white gap-1.5 shadow-sm" data-testid="home-newsletter-view">
                        <ExternalLink size={14} />Read Newsletter
                      </Button>
                    </a>
                    <Link to="/news">
                      <Button size="sm" variant="outline" className="gap-1.5 border-[#D4A84B]/30 text-[#D4A84B] hover:bg-[#D4A84B]/5" data-testid="home-newsletter-all">
                        View All Issues
                        <ArrowRight size={14} />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section className="py-12 md:py-14 bg-gradient-to-r from-[#1A202C] to-[#2D3748]" data-testid="home-careers-cta">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5 text-white">
              <div className="w-14 h-14 bg-[#4ECDC4]/15 rounded-xl flex items-center justify-center shrink-0">
                <Briefcase size={26} className="text-[#4ECDC4]" />
              </div>
              <div>
                <p className="inline-block text-xs font-medium text-[#4ECDC4] tracking-wider uppercase mb-1">We're Hiring</p>
                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-1">Join Our Team at SSMCH</h3>
                <p className="text-white/70 text-sm md:text-base max-w-xl">Teaching, clinical, administrative & support roles across our 930-bed campus. Apply in minutes.</p>
              </div>
            </div>
            <Link to="/careers" className="shrink-0">
              <Button size="lg" className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg" data-testid="home-careers-btn">
                Apply for Jobs<ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Admissions CTA */}
      <section
        className="relative py-20 md:py-28 hero-overlay"
        style={{
          backgroundImage: `url(${normalizeImageUrl(homeCms?.admissions_cta?.image) || LAB_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="cta-section">

        <div className="container-custom text-center">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            {homeCms?.admissions_cta?.badge || 'Admissions Open'}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {homeCms?.admissions_cta?.title || 'MBBS Admissions 2025-26'}
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-8">
            {homeCms?.admissions_cta?.subtitle || 'Begin your journey towards becoming a healthcare professional. Apply now for the upcoming academic session.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={homeCms?.admissions_cta?.primary_link || '/admissions'}>
              <Button
                size="lg"
                className="bg-[#D4A84B] hover:bg-[#C49A3F] text-white rounded-full px-8 h-14 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                data-testid="cta-apply-btn">

                {homeCms?.admissions_cta?.primary_label || 'Apply Now'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to={homeCms?.admissions_cta?.secondary_link || '/contact'}>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#1A202C] rounded-full px-8 h-14 text-lg font-medium transition-all"
                data-testid="cta-contact-btn">

                {homeCms?.admissions_cta?.secondary_label || 'Contact Admissions'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;